import {
  useMemo,
  useCallback,
  useRef,
  useEffect,
  useState,
  memo,
} from 'react';
import GridLayout, {
  WidthProvider,
  type Layout as RGLLayout,
} from 'react-grid-layout';
import { useAppStore } from '../../stores/appStore';
import type { WidgetLayoutItem } from '../../lib/api';
import { StickyNotesWidget } from './StickyNotesWidget';
import { TodosWidget } from './TodosWidget';
import { WeatherWidget } from './WeatherWidget';
import { RadarWidget } from './RadarWidget';
import { TrafficWidget } from './TrafficWidget';
import { UpcomingWidget } from './UpcomingWidget';
import { MessagesWidget } from './MessagesWidget';
import { SmartHomeWidget } from './SmartHomeWidget';
import { BlinkWidget } from './BlinkWidget';
import { CountdownWidget } from './CountdownWidget';

const ReactGridLayout = WidthProvider(GridLayout);
const COLS = 12;

/** Compare grid geometry only (ignore type/config noise). */
function layoutGeometryKey(items: Array<{ i: string; x: number; y: number; w: number; h: number }>): string {
  return items
    .map((it) => `${it.i}:${it.x},${it.y},${it.w},${it.h}`)
    .sort()
    .join('|');
}

function mergeFromRgl(
  next: RGLLayout[],
  prev: WidgetLayoutItem[]
): WidgetLayoutItem[] {
  const byId = new Map(prev.map((p) => [p.i, p]));
  return next.map((item) => {
    const old = byId.get(item.i);
    return {
      i: item.i,
      type: old?.type || 'sticky-notes',
      x: item.x,
      y: item.y,
      w: item.w,
      h: item.h,
      minW: item.minW ?? old?.minW ?? 2,
      minH: item.minH ?? old?.minH ?? 1,
      config: old?.config,
    };
  });
}

/**
 * Fully customizable bottom widget area.
 * Drag / resize when layout edit mode is on.
 *
 * Performance: layout is held in local state while dragging; we only
 * persist to the server on drag/resize *stop* (not on every onLayoutChange).
 * That avoids the save → re-render → WS echo → freeze loop.
 */
export function WidgetBoard() {
  const storeLayout = useAppStore((s) => s.layout);
  const saveLayout = useAppStore((s) => s.saveLayout);
  const layoutEditMode = useAppStore((s) => s.layoutEditMode);
  const setLayoutEditMode = useAppStore((s) => s.setLayoutEditMode);

  // Local working copy — avoids thrashing global store mid-drag
  const [localLayout, setLocalLayout] = useState<WidgetLayoutItem[]>(storeLayout);
  const localLayoutRef = useRef(localLayout);
  localLayoutRef.current = localLayout;
  const draggingRef = useRef(false);
  const lastSavedKey = useRef(layoutGeometryKey(storeLayout));
  const persistTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync from store only when not dragging (e.g. load, add/remove from another device)
  useEffect(() => {
    if (draggingRef.current) return;
    const incoming = layoutGeometryKey(storeLayout);
    const current = layoutGeometryKey(localLayoutRef.current);
    if (incoming !== current) {
      setLocalLayout(storeLayout);
      lastSavedKey.current = incoming;
    }
  }, [storeLayout]);

  const gridLayout: RGLLayout[] = useMemo(
    () =>
      localLayout.map((w) => ({
        i: w.i,
        x: w.x,
        y: w.y,
        w: w.w,
        h: w.h,
        minW: w.minW ?? 2,
        minH: w.minH ?? 1,
      })),
    [localLayout]
  );

  const persist = useCallback(
    (merged: WidgetLayoutItem[], immediate = false) => {
      const key = layoutGeometryKey(merged);
      if (key === lastSavedKey.current) return;
      lastSavedKey.current = key;

      if (persistTimer.current) clearTimeout(persistTimer.current);

      const doSave = () => {
        void saveLayout(merged);
      };

      if (immediate) doSave();
      else persistTimer.current = setTimeout(doSave, 250);
    },
    [saveLayout]
  );

  useEffect(
    () => () => {
      if (persistTimer.current) clearTimeout(persistTimer.current);
    },
    []
  );

  /** Live preview only — never hit the API here (fires constantly while dragging). */
  const onLayoutChange = useCallback(
    (next: RGLLayout[]) => {
      if (!layoutEditMode) return;
      setLocalLayout((prev) => {
        const merged = mergeFromRgl(next, prev);
        if (layoutGeometryKey(merged) === layoutGeometryKey(prev)) return prev;
        // While idle, only accept changes that match our item set (avoid RGL thrash)
        if (!draggingRef.current && merged.length !== prev.length) return prev;
        return merged;
      });
    },
    [layoutEditMode]
  );

  const onInteractionStart = useCallback(() => {
    draggingRef.current = true;
  }, []);

  const onInteractionStop = useCallback(
    (next: RGLLayout[]) => {
      draggingRef.current = false;
      if (!layoutEditMode) return;
      const merged = mergeFromRgl(next, localLayoutRef.current);
      setLocalLayout(merged);
      persist(merged, true);
    },
    [layoutEditMode, persist]
  );

  function removeWidget(id: string) {
    const next = localLayout.filter((w) => w.i !== id);
    setLocalLayout(next);
    persist(next, true);
  }

  function addWidget(type: string) {
    const maxY = localLayout.reduce((m, w) => Math.max(m, w.y + w.h), 0);
    const item: WidgetLayoutItem = {
      i: `w-${type}-${Date.now()}`,
      type,
      x: 0,
      y: maxY,
      w:
        type === 'countdown'
          ? 2
          : type === 'radar' || type === 'traffic'
            ? 4
            : type === 'weather'
              ? 3
              : 3,
      h: type === 'radar' || type === 'traffic' ? 3 : 2,
      minW: 2,
      minH: 1,
      config:
        type === 'countdown'
          ? {
              title: 'Countdown',
              target: new Date(Date.now() + 7 * 864e5).toISOString().slice(0, 10),
            }
          : undefined,
    };
    const next = [...localLayout, item];
    setLocalLayout(next);
    persist(next, true);
  }

  if (localLayout.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 py-8 text-slate-400">
        <p>No widgets yet</p>
        <button
          type="button"
          className="rounded-xl bg-violet-600 px-4 py-2 font-semibold text-white"
          onClick={() => {
            setLayoutEditMode(true);
            addWidget('sticky-notes');
          }}
        >
          Add widgets
        </button>
      </div>
    );
  }

  return (
    <div className="relative">
      {layoutEditMode && (
        <div className="mb-2 flex flex-wrap items-center gap-2 rounded-xl bg-amber-500/10 px-3 py-2 text-sm text-amber-200">
          <span className="font-semibold">Edit mode:</span> drag the handle bar
          to move, corner to resize. Saves when you release.
          <div className="ml-auto flex flex-wrap gap-1">
            {[
              'sticky-notes',
              'todos',
              'weather',
              'radar',
              'traffic',
              'upcoming',
              'messages',
              'smart-home',
              'blink',
              'countdown',
            ].map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => addWidget(t)}
                className="rounded-lg bg-slate-800 px-2 py-1 text-xs capitalize text-white hover:bg-slate-700"
              >
                + {t === 'traffic' ? 'traffic reports' : t.replace('-', ' ')}
              </button>
            ))}
          </div>
        </div>
      )}

      <ReactGridLayout
        className="layout"
        layout={gridLayout}
        cols={COLS}
        rowHeight={88}
        margin={[12, 12]}
        containerPadding={[0, 0]}
        onLayoutChange={onLayoutChange}
        onDragStart={onInteractionStart}
        onResizeStart={onInteractionStart}
        onDragStop={onInteractionStop}
        onResizeStop={onInteractionStop}
        isDraggable={layoutEditMode}
        isResizable={layoutEditMode}
        // Avoid aggressive reflow loops while editing
        compactType={layoutEditMode ? null : 'vertical'}
        preventCollision={false}
        useCSSTransforms
        draggableHandle=".widget-drag-handle"
        // Don't re-measure on every child re-render
        measureBeforeMount={false}
      >
        {localLayout.map((w) => (
          <div
            key={w.i}
            className="overflow-hidden rounded-2xl border border-slate-700/70 bg-slate-900/90 shadow-lg light:border-slate-200 light:bg-white"
          >
            {layoutEditMode && (
              <div className="widget-drag-handle flex cursor-move items-center justify-between border-b border-slate-800 bg-slate-800/50 px-2 py-1 text-xs text-slate-400">
                <span className="capitalize">{w.type.replace('-', ' ')}</span>
                <button
                  type="button"
                  className="rounded px-1.5 py-0.5 text-red-400 hover:bg-red-500/20"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeWidget(w.i);
                  }}
                >
                  Remove
                </button>
              </div>
            )}
            <div className="h-full overflow-auto p-3">
              <WidgetRenderer type={w.type} config={w.config} />
            </div>
          </div>
        ))}
      </ReactGridLayout>
    </div>
  );
}

const WidgetRenderer = memo(function WidgetRenderer({
  type,
  config,
}: {
  type: string;
  config?: Record<string, unknown>;
}) {
  switch (type) {
    case 'sticky-notes':
      return <StickyNotesWidget />;
    case 'todos':
      return <TodosWidget />;
    case 'weather':
      return <WeatherWidget />;
    case 'radar':
      return <RadarWidget />;
    case 'traffic':
      return <TrafficWidget />;
    case 'upcoming':
      return <UpcomingWidget />;
    case 'messages':
      return <MessagesWidget />;
    case 'smart-home':
      return <SmartHomeWidget />;
    case 'blink':
      return <BlinkWidget />;
    case 'countdown':
      return <CountdownWidget config={config} />;
    default:
      return <p className="text-sm text-slate-500">Unknown widget: {type}</p>;
  }
});
